'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { ValidationMessage } from '@/ui/form/validation-message/ValidationMessage.client'
import { FieldError, RadioGroup } from 'react-aria-components'
import { AbstractInput } from '../../_shared/AbstractInput.client'
import { RadioCard } from './RadioCard.client'
import type { RadioCardGroupProps } from './RadioCard.types'

export function RadioCardGroup({
  name,
  label,
  options = [],
  children,
  isLoading = false,
  isReadOnly,
  className,
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId = 'radio-card-group',
  ...rules
}: RadioCardGroupProps) {
  if (!options.length && !children) return null

  return (
    <AbstractInput
      {...rules}
      name={name}
      render={({ field, fieldState }) => (
        <RadioGroup
          {...field}
          id={id}
          name={field.name}
          // react-hook-form starts an untouched field at '', which react-aria reads as a selection
          // that matches no card, leaving every radio at tabindex -1 and the group out of the tab order
          value={field.value || null}
          aria-label={ariaLabel ?? label}
          isDisabled={isLoading || field.isDisabled}
          isReadOnly={isReadOnly}
          isRequired={fieldState.isRequired}
          isInvalid={!!fieldState.error}
          data-testid={dataTestId}
          className={twMerge('flex flex-col gap-2', className)}
        >
          {options.map((option) => (
            <RadioCard key={option.value} data-testid={`${dataTestId}/option`} {...option} />
          ))}

          {children}

          <FieldError>
            {fieldState.error?.message && (
              <ValidationMessage status="error" data-testid={`${dataTestId}/error`}>
                {fieldState.error.message}
              </ValidationMessage>
            )}
          </FieldError>
        </RadioGroup>
      )}
    />
  )
}
